import {
  Download,
  Loader2,
  Mail,
  Phone,
  Plus,
  Send,
  ShieldAlert,
} from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  CLIENT_COMMS_EVENTS,
  CLIENT_COMMS_PROPERTIES,
} from '@/constants/analytics/client-communications-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';
import { useTreatmentSendSmsMutation } from '@/services/api/generated/treatmentSmsService';
import { usePatchMatterClientMutation } from '@/services/api/mattersService';
import { ClientCommunicationsTabId } from '@/services/types/client-communications-types';

import { useClientCommunications } from './ClientCommunicationsContext';

interface CommunicationsFooterProps {
  activeTab: ClientCommunicationsTabId;
  matterId: string;
  firmId?: string;
  clientId?: string;
  clientPhone?: string;
  clientEmail?: string;
  clientName: string;
  doNotContact?: boolean;
  smsConfigured?: boolean;
  onOpenEmailComposer: () => void;
  onInitiateCall: () => void;
  onContactAdded?: () => void;
}

export const CommunicationsFooter: React.FC<CommunicationsFooterProps> = ({
  activeTab,
  matterId,
  firmId,
  clientId,
  clientPhone,
  clientEmail,
  clientName,
  doNotContact,
  smsConfigured = true,
  onOpenEmailComposer,
  onInitiateCall,
  onContactAdded,
}) => {
  const {
    selectedAttachments,
    attachments,
    clearAttachmentSelection,
    refreshCommunications,
    addPendingMessage,
    markPendingMessageSent,
    markPendingMessageFailed,
  } = useClientCommunications();

  const capture = usePostHogCapture();

  const [message, setMessage] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showDncConfirm, setShowDncConfirm] = useState(false);
  const [sendSms, { isLoading: isSending }] = useTreatmentSendSmsMutation();
  const [patchClient, { isLoading: isSavingContact }] =
    usePatchMatterClientMutation();

  const doSendSms = useCallback(async () => {
    if (!message.trim() || !clientPhone || isSending) return;

    const messageText = message.trim();
    setMessage(''); // Clear immediately for optimistic feel
    setShowDncConfirm(false);

    // Add optimistic pending message
    const pendingId = addPendingMessage(messageText);

    try {
      await sendSms({
        sendTreatmentSmsRequest: {
          matter_id: matterId,
          message_text: messageText,
        },
      }).unwrap();

      // Mark pending message as sent - it will be automatically removed
      // when the real message arrives from the webhook via polling
      markPendingMessageSent(pendingId);
      refreshCommunications();

      // Track successful SMS send
      capture(CLIENT_COMMS_EVENTS.SMS_SENT, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
        [CLIENT_COMMS_PROPERTIES.MESSAGE_LENGTH]: messageText.length,
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    } catch (err) {
      // Mark pending message as failed with backend error message
      const smsError = err as {
        data?: { detail?: string; message?: string; error?: string };
      };
      const errorMessage =
        smsError?.data?.detail ||
        smsError?.data?.message ||
        smsError?.data?.error ||
        'Failed to send message';
      markPendingMessageFailed(pendingId, errorMessage);
      toast.error(errorMessage);

      // Track failed SMS send
      capture(CLIENT_COMMS_EVENTS.SMS_SEND_FAILED, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
        [CLIENT_COMMS_PROPERTIES.MESSAGE_LENGTH]: messageText.length,
        [CLIENT_COMMS_PROPERTIES.ERROR_MESSAGE]: errorMessage,
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    }
  }, [
    message,
    clientPhone,
    isSending,
    matterId,
    sendSms,
    refreshCommunications,
    addPendingMessage,
    markPendingMessageSent,
    markPendingMessageFailed,
    capture,
  ]);

  const handleSendSms = useCallback(() => {
    if (!message.trim() || !clientPhone || isSending) return;

    if (doNotContact) {
      setShowDncConfirm(true);
    } else {
      doSendSms();
    }
  }, [message, clientPhone, isSending, doNotContact, doSendSms]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendSms();
      }
    },
    [handleSendSms],
  );

  const handleDownloadSelected = () => {
    const selectedFiles = attachments.filter((a) =>
      selectedAttachments.has(a.id),
    );
    selectedFiles.forEach((file) => {
      const link = document.createElement('a');
      link.href = file.download_url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
    clearAttachmentSelection();
    toast.success(`Downloaded ${selectedFiles.length} file(s)`);

    // Track bulk download
    capture(CLIENT_COMMS_EVENTS.BULK_DOWNLOAD_CLICKED, {
      [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
      [CLIENT_COMMS_PROPERTIES.ATTACHMENT_COUNT]: selectedFiles.length,
      [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
      [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
    });
  };

  const handleSavePhone = useCallback(async () => {
    if (!newPhone.trim() || !clientId || isSavingContact) return;

    try {
      // Build patch preserving existing email
      const patch = {
        phone_numbers: [{ phone_number: newPhone.trim() }],
        ...(clientEmail && { email: clientEmail }),
      } as Parameters<typeof patchClient>[0]['patch'];

      await patchClient({
        matter_id: matterId,
        matter_client_id: clientId,
        patch,
      }).unwrap();

      setNewPhone('');
      toast.success('Phone number saved');
      onContactAdded?.();

      // Track phone number added
      capture(CLIENT_COMMS_EVENTS.PHONE_NUMBER_ADDED, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    } catch (err) {
      const error = err as Error;
      toast.error(error.message || 'Failed to save phone number');
    }
  }, [
    newPhone,
    clientId,
    matterId,
    clientEmail,
    patchClient,
    isSavingContact,
    onContactAdded,
    capture,
  ]);

  const handleSaveEmail = useCallback(async () => {
    if (!newEmail.trim() || !clientId || isSavingContact) return;

    try {
      // Build patch preserving existing phone
      const patch = {
        email: newEmail.trim(),
        ...(clientPhone && { phone_numbers: [{ phone_number: clientPhone }] }),
      } as Parameters<typeof patchClient>[0]['patch'];

      await patchClient({
        matter_id: matterId,
        matter_client_id: clientId,
        patch,
      }).unwrap();

      setNewEmail('');
      toast.success('Email saved');
      onContactAdded?.();

      // Track email added
      capture(CLIENT_COMMS_EVENTS.EMAIL_ADDED, {
        [CLIENT_COMMS_PROPERTIES.MATTER_ID]: matterId,
        [CLIENT_COMMS_PROPERTIES.PAGE]: 'matter_detail',
        [CLIENT_COMMS_PROPERTIES.COMPONENT]: 'client_communications',
      });
    } catch (err) {
      const error = err as Error;
      toast.error(error.message || 'Failed to save email');
    }
  }, [
    newEmail,
    clientId,
    matterId,
    clientPhone,
    patchClient,
    isSavingContact,
    onContactAdded,
    capture,
  ]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    // Set to scrollHeight but cap at 5 lines (~120px)
    const newHeight = Math.min(textarea.scrollHeight, 120);
    textarea.style.height = `${newHeight}px`;
  }, []);

  const handleMessageChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value);
      adjustTextareaHeight();
    },
    [adjustTextareaHeight],
  );

  // SMS Input Footer (for All and Texts tabs)
  if (activeTab === 'all' || activeTab === 'texts') {
    // Show DNC message when contact is disabled
    if (doNotContact) {
      return (
        <div className="border-t border-zinc-200 bg-zinc-50 p-3">
          <div className="flex items-center justify-center gap-2 text-zinc-500">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            <p className="text-xs font-semibold">
              Outbound contact is disabled for this matter
            </p>
          </div>
        </div>
      );
    }

    // Show add phone capsule if no phone number
    if (!clientPhone && clientId) {
      return (
        <div className="border-t border-zinc-100 bg-white p-3">
          <div className="flex items-center bg-white rounded-3xl border-2 border-dashed border-primary/50 transition-all duration-200">
            <div className="flex items-center justify-center py-2 pl-3 pr-2 text-primary/60">
              <Phone className="h-4 w-4 shrink-0" />
            </div>
            <input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSavePhone();
                }
              }}
              placeholder="Add phone number to send messages..."
              className={cn(
                'flex-1 min-w-0 bg-transparent',
                'text-sm text-zinc-900 font-semibold placeholder:text-primary/50',
                'py-2.5 pr-2',
                'border-0 outline-none focus:outline-none focus:ring-0',
              )}
            />
            <Button
              onClick={handleSavePhone}
              disabled={!newPhone.trim() || isSavingContact}
              size="icon"
              className={cn(
                'h-8 w-8 shrink-0 rounded-full m-1 mr-1.5',
                'bg-primary hover:bg-primary/90',
                'transition-all duration-200',
              )}
            >
              {isSavingContact ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-[9px] text-primary/50 mt-0.5 px-2 font-semibold">
            Press Enter to save
          </p>
        </div>
      );
    }

    // Show SMS not configured message
    if (!smsConfigured) {
      return (
        <div className="border-t border-zinc-100 bg-amber-50 p-3">
          <div className="flex items-center gap-2 text-amber-700">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            <p className="text-xs font-semibold">
              SMS sending is not configured for this firm.{' '}
              {firmId ? (
                <Link
                  to={`/firms/${firmId}`}
                  className="underline hover:text-amber-900"
                >
                  Configure SMS Sender Number →
                </Link>
              ) : (
                'Contact your administrator to set up the SMS sender number.'
              )}
            </p>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="border-t border-zinc-100 bg-white p-3">
          <div
            className={cn(
              'flex items-end bg-zinc-100 rounded-3xl',
              'transition-all duration-200',
            )}
          >
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleMessageChange}
              onKeyDown={handleKeyDown}
              placeholder="Send an SMS to the client..."
              disabled={isSending}
              className={cn(
                'flex-1 min-w-0 resize-none bg-transparent',
                'text-sm text-zinc-900 font-semibold placeholder:text-zinc-400',
                'py-2.5 pl-3 pr-2',
                'border-0 outline-none focus:outline-none focus:ring-0',
                'leading-5',
              )}
              style={{
                minHeight: '40px',
                maxHeight: '120px',
              }}
              rows={1}
            />
            <Button
              onClick={handleSendSms}
              disabled={!message.trim() || isSending}
              size="icon"
              className={cn(
                'h-8 w-8 shrink-0 rounded-full m-1 mr-1.5',
                'bg-primary hover:bg-primary/90',
                'transition-all duration-200',
              )}
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-[9px] text-zinc-400 mt-0.5 px-2 font-semibold">
            Shift + Enter for new line
          </p>
        </div>

        {/* DNC Confirmation Dialog */}
        <AlertDialog
          open={showDncConfirm}
          onOpenChange={(open) => !open && setShowDncConfirm(false)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <div className="flex items-center gap-2 text-red-600 mb-2">
                <ShieldAlert className="h-5 w-5" />
                <AlertDialogTitle className="text-red-600">
                  Do Not Contact Warning
                </AlertDialogTitle>
              </div>
              <AlertDialogDescription className="text-zinc-600">
                This matter is marked as{' '}
                <span className="font-semibold text-red-600">
                  Do Not Contact Client
                </span>
                . Are you sure you want to send a text message to{' '}
                <span className="font-semibold">{clientName}</span>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={doSendSms}
                className="bg-red-600 hover:bg-red-700"
              >
                Yes, Send Message
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  // Email action footer
  if (activeTab === 'emails') {
    // Show DNC message when contact is disabled
    if (doNotContact) {
      return (
        <div className="border-t border-zinc-200 bg-zinc-50 p-3">
          <div className="flex items-center justify-center gap-2 text-zinc-500">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            <p className="text-xs font-semibold">
              Outbound contact is disabled for this matter
            </p>
          </div>
        </div>
      );
    }

    // Show add email capsule if no email
    if (!clientEmail && clientId) {
      return (
        <div className="border-t border-zinc-100 bg-white p-3">
          <div className="flex items-center bg-white rounded-3xl border-2 border-dashed border-primary/50 transition-all duration-200">
            <div className="flex items-center justify-center py-2 pl-3 pr-2 text-primary/60">
              <Mail className="h-4 w-4 shrink-0" />
            </div>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveEmail();
                }
              }}
              placeholder="Add email to send messages..."
              className={cn(
                'flex-1 min-w-0 bg-transparent',
                'text-sm text-zinc-900 font-semibold placeholder:text-primary/50',
                'py-2.5 pr-2',
                'border-0 outline-none focus:outline-none focus:ring-0',
              )}
            />
            <Button
              onClick={handleSaveEmail}
              disabled={!newEmail.trim() || isSavingContact}
              size="icon"
              className={cn(
                'h-8 w-8 shrink-0 rounded-full m-1 mr-1.5',
                'bg-primary hover:bg-primary/90',
                'transition-all duration-200',
              )}
            >
              {isSavingContact ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-[9px] text-primary/50 mt-0.5 px-2 font-semibold">
            Press Enter to save
          </p>
        </div>
      );
    }

    return (
      <div className="border-t border-zinc-100 bg-white p-3">
        <Button onClick={onOpenEmailComposer} className="w-full h-9">
          <Mail className="h-4 w-4 mr-2" />
          Compose Email
        </Button>
      </div>
    );
  }

  // Call action footer
  if (activeTab === 'calls') {
    // Show DNC message when contact is disabled
    if (doNotContact) {
      return (
        <div className="border-t border-zinc-200 bg-zinc-50 p-3">
          <div className="flex items-center justify-center gap-2 text-zinc-500">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            <p className="text-xs font-semibold">
              Outbound contact is disabled for this matter
            </p>
          </div>
        </div>
      );
    }

    // Show add phone capsule if no phone number
    if (!clientPhone && clientId) {
      return (
        <div className="border-t border-zinc-100 bg-white p-3">
          <div className="flex items-center bg-white rounded-3xl border-2 border-dashed border-primary/50 transition-all duration-200">
            <div className="flex items-center justify-center py-2 pl-3 pr-2 text-primary/60">
              <Phone className="h-4 w-4 shrink-0" />
            </div>
            <input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSavePhone();
                }
              }}
              placeholder="Add phone number to make calls..."
              className={cn(
                'flex-1 min-w-0 bg-transparent',
                'text-sm text-zinc-900 font-semibold placeholder:text-primary/50',
                'py-2.5 pr-2',
                'border-0 outline-none focus:outline-none focus:ring-0',
              )}
            />
            <Button
              onClick={handleSavePhone}
              disabled={!newPhone.trim() || isSavingContact}
              size="icon"
              className={cn(
                'h-8 w-8 shrink-0 rounded-full m-1 mr-1.5',
                'bg-primary hover:bg-primary/90',
                'transition-all duration-200',
              )}
            >
              {isSavingContact ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-[9px] text-primary/50 mt-0.5 px-2 font-semibold">
            Press Enter to save
          </p>
        </div>
      );
    }

    return (
      <div className="border-t border-zinc-100 bg-white p-3">
        <Button onClick={onInitiateCall} className="w-full h-9">
          <Phone className="h-4 w-4 mr-2" />
          Call {clientName}
        </Button>
      </div>
    );
  }

  // Attachments download footer
  if (activeTab === 'attachments') {
    const count = selectedAttachments.size;
    return (
      <div className="border-t border-zinc-100 bg-white p-3">
        <Button
          disabled={count === 0}
          onClick={handleDownloadSelected}
          className="w-full h-9"
          variant={count > 0 ? 'default' : 'secondary'}
        >
          <Download className="h-4 w-4 mr-2" />
          {count > 0
            ? `Download ${count} file${count !== 1 ? 's' : ''}`
            : 'Select files to download'}
        </Button>
      </div>
    );
  }

  return null;
};

